import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";
import { Feedback } from "./types/queries";
import { RootState } from "../../app/store";
import { createQueryAsyncThunk } from "../../app/createApiAsyncThunk";
import {
  Hits,
  Paginated,
  PaginationFilters,
  Relation,
} from "../../common/types/Paginated";
import {
  ackCustomerFeedbackToEntry,
  feedbacks,
  mockFeedbacks,
  unacknowledgedFeedbacksCount,
} from "./FeedbacksAPI";
import { WorktimeEntryCustomerFeedbackAcknowledgedThunk } from "./types/types";
import { AckCustomerFeedbackToEntry } from "./types/commands";
import { EventPayload } from "../../common/types/Event";
import { WorktimeEntryCustomerFeedbackAcknowledged } from "./types/events";
import isEmpty from "../../common/helpers/isEmpty";

type PaginatedSliceMeta = {
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
  cursor?: string;
  hits: Hits;
  unacknowledgedFeedbacksCount: number;
};

export interface FeedbacksState
  extends EntityState<Feedback, string>,
    PaginatedSliceMeta {}

const initialState: FeedbacksState = {
  ids: [],
  entities: {},
  status: "idle",
  error: undefined,
  cursor: undefined,
  hits: { value: 0, relation: Relation.DEFAULT },
  unacknowledgedFeedbacksCount: 0,
};

export const fetchFeedbacks = createQueryAsyncThunk<
  Paginated<Feedback[]>,
  PaginationFilters
>("feedbacks/fetchFeedbacks", feedbacks);

export const fetchUnacknowledgedFeedbacksCount = createQueryAsyncThunk<
  Paginated<Feedback[]>,
  {}
>("feedbacks/fetchUnacknowledgedFeedbacksCount", unacknowledgedFeedbacksCount);

export const fetchMockFeedbacks = createQueryAsyncThunk<
  Paginated<Feedback[]>,
  PaginationFilters
>("feedbacks/fetchMockFeedbacks", mockFeedbacks);

export const ackCustomerFeedbackEntry = createAsyncThunk<
  WorktimeEntryCustomerFeedbackAcknowledgedThunk,
  AckCustomerFeedbackToEntry,
  { state: RootState; rejectValue: string }
>(
  "feedbacks/ackCustomerFeedbackEntry",
  async (filters, { getState, requestId }) => {
    const response: EventPayload<WorktimeEntryCustomerFeedbackAcknowledged>[] =
      await ackCustomerFeedbackToEntry(filters, { requestId });
    const eventPayload = response && !isEmpty(response) ? response[0] : null;

    const userId = getState().user.user?.id as string;
    return {
      worktimeEntryId: filters.id,
      ackByUserId: userId,
      sentiment: eventPayload?.payload.sentiment,
      relatesTo: eventPayload?.payload.relatesTo,
    };
  },
);

export const feedbacksAdapter = createEntityAdapter<Feedback>();

const feedbackSlice = createSlice({
  name: "feedbacks",
  initialState: { ...initialState },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeedbacks.fulfilled, (state, action) => {
        if (action.payload.cursor) {
          feedbacksAdapter.upsertMany(state, action.payload.data);
        } else {
          feedbacksAdapter.setAll(state, action.payload.data);
        }

        state.hits = action.payload.hits;
        state.cursor = action.payload.cursor;
        state.status = "succeeded";
      })
      .addCase(fetchFeedbacks.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchFeedbacks.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchMockFeedbacks.fulfilled, (state, action) => {
        feedbacksAdapter.setAll(state, action.payload.data);
        state.cursor = action.payload.cursor;
        state.status = "succeeded";
      })
      .addCase(fetchMockFeedbacks.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchMockFeedbacks.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(ackCustomerFeedbackEntry.fulfilled, (state, action) => {
        const { worktimeEntryId, ackByUserId, relatesTo, sentiment } =
          action.payload;

        const feedbackEntity = Object.values(state.entities).find(
          (feedback) => feedback?.worktimeEntry?.id === worktimeEntryId,
        );

        if (feedbackEntity) {
          feedbacksAdapter.updateOne(state, {
            id: feedbackEntity.id,
            changes: { ackByUserId, sentiment, relatesTo },
          });

          state.unacknowledgedFeedbacksCount -= 1;
        }

        state.status = "succeeded";
      })
      .addCase(ackCustomerFeedbackEntry.pending, (state) => {
        state.status = "pending";
      })
      .addCase(ackCustomerFeedbackEntry.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchUnacknowledgedFeedbacksCount.fulfilled, (state, action) => {
        state.unacknowledgedFeedbacksCount = action.payload.hits.value;
        state.status = "succeeded";
      })
      .addCase(fetchUnacknowledgedFeedbacksCount.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchUnacknowledgedFeedbacksCount.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export const { selectById: selectFeedbackById, selectAll: selectAllFeedbacks } =
  feedbacksAdapter.getSelectors((state: RootState) => state.feedbacks);

const selectFeedbacksState = (state: RootState) => state.feedbacks;

export const selectUnacknowledgedFeedbacksCount = (state: RootState) =>
  selectFeedbacksState(state).unacknowledgedFeedbacksCount;

export const feedbacksReducer = feedbackSlice.reducer;
